import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight, Loader2, PackageCheck, Plus, RefreshCw, Search, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { RELEASE_PRODUCT_LABELS, formatDate, productErrorMessage, proofText, releaseConfirmation } from "../lib/product";
import useRunningVersions from "../hooks/useRunningVersions";
import { ConfirmationBadge, ReleaseStateBadge } from "../components/product/ProductBits";
import { ReleaseFormDialog } from "../components/product/ReleaseDialogs";
import RunningVersions from "../components/product/RunningVersions";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ALL = "__all__";
const PAGE_SIZE = 50;
const STATES = [{ id: "", label: "Toutes" }, { id: "planned", label: "Prévues" }, { id: "delivered", label: "Livrées" }];

function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters = {
    state: ["planned", "delivered"].includes(params.get("state")) ? params.get("state") : "",
    product: Object.keys(RELEASE_PRODUCT_LABELS).includes(params.get("product")) ? params.get("product") : "",
    search: params.get("q") || "",
  };
  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        const name = key === "search" ? "q" : key;
        if (value === "" || value == null) next.delete(name);
        else next.set(name, value);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [filters, update];
}

function MobileRow({ release, confirmedIds }) {
  return (
    <Link to={`/produit/versions/${release.id}`} className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`release-card-${release.number}`}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="text-xs text-slate-500 tabular-nums">{release.number} · {RELEASE_PRODUCT_LABELS[release.product]}</div>
        <div className="font-medium text-slate-900 break-words">{release.label}</div>
        <div className="flex flex-wrap gap-1.5"><ReleaseStateBadge state={release.state} /><ConfirmationBadge confirmation={releaseConfirmation(release, confirmedIds)} /></div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
          {release.deployed_at && <span>Déployée le {formatDate(release.deployed_at)}</span>}
          <span>{release.items_count} élément{release.items_count > 1 ? "s" : ""}</span>
        </div>
      </div>
      <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
    </Link>
  );
}

// Versions livrées (D-30): what was delivered, with which proof, and whether production confirms it.
export default function ProductReleases() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const running = useRunningVersions();
  const [filters, update] = useFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [data, setData] = useState({ releases: [], total: 0, running: {} });
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [creating, setCreating] = useState(false);
  const seq = useRef(0);
  const canManage = hasPermission(user, "product:manage_releases");

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== filters.search) update({ search: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const out = { limit: PAGE_SIZE };
    for (const key of ["state", "product", "search"]) if (filters[key]) out[key] = filters[key];
    return out;
  }, [filters.state, filters.product, filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get("/product/releases", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setErrorText(productErrorMessage(e, "Impossible de charger les versions."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const loadMore = async () => {
    if (loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/product/releases", { params: { ...query, skip: data.releases.length } });
      if (mine !== seq.current) return;
      setData((prev) => ({ ...body, releases: [...prev.releases, ...body.releases.filter((r) => !prev.releases.some((p) => p.id === r.id))] }));
    } catch (e) {
      if (mine === seq.current) toast.error(productErrorMessage(e, "Impossible de charger la suite."));
    } finally {
      if (mine === seq.current) setLoadingMore(false);
    }
  };

  const filtered = !!filters.state || !!filters.product || !!filters.search;
  const reset = () => { setSearchInput(""); update({ state: "", product: "", search: "" }); };
  const runningCrm = data.running?.crm;

  return (
    <div className="space-y-4" data-testid="releases-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Versions livrées</h1>
          <p className="text-sm text-slate-500">Ce qui a été déployé, avec la preuve de déploiement donnée par une personne.</p>
          {runningCrm && <p className="text-xs text-slate-500 mt-1" data-testid="releases-running">Commit du CRM en cours : <span className="font-mono">{runningCrm.slice(0, 7)}</span></p>}
        </div>
        <div className="flex items-center gap-2">
          {canManage && <Button onClick={() => setCreating(true)} data-testid="release-new"><Plus size={16} className="mr-1.5" />Nouvelle version</Button>}
        </div>
      </div>

      <RunningVersions running={running} onRegistered={load} />

      <div className="flex gap-1" role="tablist" aria-label="État des versions" data-testid="releases-states">
        {STATES.map((s) => (
          <button key={s.id || "all"} type="button" role="tab" aria-selected={filters.state === s.id} onClick={() => update({ state: s.id })} data-testid={`releases-state-${s.id || "all"}`}
            className={`flex-auto sm:flex-none rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${filters.state === s.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro de version…" className="pl-9 h-9" aria-label="Rechercher une version" data-testid="releases-search" />
        </div>
        <Select value={filters.product || ALL} onValueChange={(v) => update({ product: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-full sm:w-56" aria-label="Logiciel" data-testid="releases-product-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les logiciels</SelectItem>
            {Object.entries(RELEASE_PRODUCT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset} data-testid="releases-reset"><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2" data-testid="releases-loading"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="releases-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{errorText}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}

      {phase === "ready" && data.releases.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="releases-empty">
          <PackageCheck className="mx-auto text-slate-300" size={26} />
          {filtered ? (
            <>
              <h3 className="font-medium text-slate-900">Aucune version ne correspond</h3>
              <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button>
            </>
          ) : (
            <>
              <h3 className="font-medium text-slate-900">Aucune version enregistrée</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Une version regroupe les retours livrés ensemble. Elle devient « livrée » avec la preuve de son déploiement.</p>
              {canManage && <Button size="sm" onClick={() => setCreating(true)}><Plus size={14} className="mr-1.5" />Nouvelle version</Button>}
            </>
          )}
        </Card>
      )}

      {phase === "ready" && data.releases.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="releases-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead><TableHead>Logiciel</TableHead><TableHead>État</TableHead><TableHead>Preuve</TableHead>
                  <TableHead>Déployée le</TableHead><TableHead>Éléments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.releases.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/produit/versions/${r.id}`)} data-testid={`release-row-${r.number}`}>
                    <TableCell>
                      <Link to={`/produit/versions/${r.id}`} className="font-medium text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>{r.label}</Link>
                      <div className="text-xs text-slate-500 tabular-nums">{r.number}</div>
                    </TableCell>
                    <TableCell className="text-sm">{RELEASE_PRODUCT_LABELS[r.product]}</TableCell>
                    <TableCell><div className="flex flex-wrap gap-1.5"><ReleaseStateBadge state={r.state} /><ConfirmationBadge confirmation={releaseConfirmation(r, running.confirmedIds)} /></div></TableCell>
                    <TableCell className="text-sm font-mono">{r.deploy_ref ? proofText(r) : <span className="text-slate-400 font-sans">—</span>}</TableCell>
                    <TableCell className="text-sm tabular-nums">{r.deployed_at ? formatDate(r.deployed_at) : <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-sm tabular-nums">{r.items_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden overflow-hidden" data-testid="releases-list-mobile">{data.releases.map((r) => <MobileRow key={r.id} release={r} confirmedIds={running.confirmedIds} />)}</Card>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="releases-count">{data.releases.length} sur {data.total} version{data.total > 1 ? "s" : ""}</span>
            {data.releases.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="releases-load-more">
                {loadingMore ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}Charger plus
              </Button>
            )}
          </div>
        </>
      )}

      <ReleaseFormDialog open={creating} onOpenChange={setCreating} onDone={(release) => { setCreating(false); toast.success(`Version ${release.label} créée`); navigate(`/produit/versions/${release.id}`); }} />
    </div>
  );
}
